import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import usePopupStore from "@/hooks/usePopupStore";
import InputGroups from "@/UI/components/InputGroups";
import ConfirmPopup, { type ConfirmPopupDetails } from "@/UI/popups/ConfirmPopup";
import { TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import "./styles.css";

// ── Types ─────────────────────────────────────────────────────────────────────

interface LangValue {
    lang: string;
    value: string;
    default?: boolean;
}

interface InitialTierData {
    _id?: string;
    name: LangValue[];
    description: LangValue[];
    start: number;
    redeemRate: number;
}

export interface TiersFormDetails {
    tierId?: string;
    initialData?: InitialTierData;
    isDuplicate?: boolean;
    onSuccess: () => void;
}

interface FormState {
    nameAr: string;
    nameEn: string;
    descriptionAr: string;
    descriptionEn: string;
    start: string;
    redeemRate: string;
}

// ── MUI Shared Styles ─────────────────────────────────────────────────────────

const getTextFieldSx = (dir: "rtl" | "ltr") => ({
    direction: dir,
    "& .MuiInputLabel-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13.5px",
        color: "var(--color-gray-02)",
        transformOrigin: dir === "rtl" ? "top right" : "top left",
        right: dir === "rtl" ? 0 : "unset",
        left: dir === "rtl" ? "unset" : 0,
        "&:not(.MuiInputLabel-shrink)": {
            transform: dir === "rtl" ? "translate(-14px, 14px) scale(1)" : "translate(14px, 14px) scale(1)",
        },
        "&.MuiInputLabel-shrink": {
            transform: dir === "rtl" ? "translate(-14px, -9px) scale(0.75)" : "translate(14px, -9px) scale(0.75)",
        },
        "&.Mui-focused": {
            color: "var(--color-orange)",
        },
    },
    "& .MuiOutlinedInput-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13.5px",
        color: "var(--color-dark)",
        backgroundColor: "var(--color-white)",
        borderRadius: "var(--radius-lev3)",
        "& fieldset": {
            borderColor: "var(--color-gray-01)",
            borderWidth: "1.5px",
        },
        "& fieldset legend": {
            textAlign: dir === "rtl" ? "right" : "left",
            marginLeft: dir === "rtl" ? "auto" : undefined,
            marginRight: dir === "rtl" ? "0px" : undefined,
        },
        "&:hover fieldset": {
            borderColor: "var(--color-gray-02)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "var(--color-orange)",
            borderWidth: "1.5px",
        },
    },
    "& .MuiOutlinedInput-input": {
        fontFamily: "Alexandria, sans-serif",
        padding: "14px",
        textAlign: dir === "rtl" ? "right" : "left",
    },
});

// ── Helpers ───────────────────────────────────────────────────────────────────

function getLangValue(arr: LangValue[] | undefined, lang: string): string {
    if (!arr || !Array.isArray(arr)) return "";
    const found = arr.find((v) => v.lang === lang);
    return found?.value || "";
}

function buildInitialForm(initial: InitialTierData | undefined): FormState {
    if (!initial) {
        return {
            nameAr: "",
            nameEn: "",
            descriptionAr: "",
            descriptionEn: "",
            start: "0",
            redeemRate: "10",
        };
    }

    return {
        nameAr: getLangValue(initial.name, "ar"),
        nameEn: getLangValue(initial.name, "en"),
        descriptionAr: getLangValue(initial.description, "ar"),
        descriptionEn: getLangValue(initial.description, "en"),
        start: String(initial.start ?? 0),
        redeemRate: String(initial.redeemRate ?? 10),
    };
}

function buildRequestData(form: FormState) {
    const name: LangValue[] = [];
    if (form.nameAr.trim()) name.push({ lang: "ar", value: form.nameAr.trim(), default: true });
    if (form.nameEn.trim()) name.push({ lang: "en", value: form.nameEn.trim() });

    const description: LangValue[] = [];
    if (form.descriptionAr.trim()) description.push({ lang: "ar", value: form.descriptionAr.trim(), default: true });
    if (form.descriptionEn.trim()) description.push({ lang: "en", value: form.descriptionEn.trim() });

    return {
        name,
        description,
        start: parseFloat(form.start) || 0,
        redeemRate: parseInt(form.redeemRate) || 10,
    };
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
    onClose: () => void;
    details?: TiersFormDetails;
}

export default function TiersForm({ onClose, details }: Props) {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const { addComp, removeComp } = usePopupStore();

    const tierId = details?.tierId;
    const isDuplicate = details?.isDuplicate;
    const isEdit = !!tierId && !isDuplicate;

    const [form, setForm] = useState<FormState>(() => buildInitialForm(details?.initialData));

    // ── Helpers ───────────────────────────────────────────────────────────────

    const showError = (message: string) => {
        const name = "tier-form-error";
        removeComp(name);
        addComp({
            name,
            Component: ConfirmPopup,
            details: {
                message,
                variant: "danger",
                confirmLabel: t.tierForm.ok,
                onConfirm: () => {},
            } as ConfirmPopupDetails,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    // ── Submit fetch ──────────────────────────────────────────────────────────

    const { loading: submitting, error: submitError, status: submitStatus, invoke: submitInvoke } = useFetch("tiers", {}, false);

    const wasSubmitting = useRef(false);
    useEffect(() => {
        if (submitting) {
            wasSubmitting.current = true;
        } else if (wasSubmitting.current) {
            wasSubmitting.current = false;
            if (submitStatus === 201 || submitStatus === 200) {
                details?.onSuccess?.();
                onClose();
            } else if (submitError) {
                showError(submitError);
            }
        }
    }, [submitting]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!form.nameAr.trim() && !form.nameEn.trim()) {
            showError(t.tierForm.nameRequired);
            return;
        }

        const data = buildRequestData(form);

        submitInvoke({
            method: isEdit ? "PUT" : "POST",
            ...(isEdit ? { param: tierId } : {}),
            data,
        });
    };

    // ── Derived ────────────────────────────────────────────────────────────────

    const dir = lang === "ar" ? "rtl" : "ltr";
    const textFieldSxRtl = getTextFieldSx("rtl");
    const textFieldSxLtr = getTextFieldSx("ltr");

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <form className="tiers-form" onSubmit={handleSubmit} noValidate>
            <div className="tiers-form-fields">
                {/* ── Name Section ── */}
                <InputGroups icon="/icons/inputs-group-icon.svg" title={t.tierForm.nameSection}>
                    <div className="tf-row tf-row--2">
                        <TextField
                            fullWidth
                            label={`${t.tierForm.name} (${t.tierForm.arabic})`}
                            value={form.nameAr}
                            onChange={(e) => setField("nameAr", e.target.value)}
                            sx={textFieldSxRtl}
                        />
                        <TextField
                            fullWidth
                            label={`${t.tierForm.name} (${t.tierForm.english})`}
                            value={form.nameEn}
                            onChange={(e) => setField("nameEn", e.target.value)}
                            sx={textFieldSxLtr}
                        />
                    </div>
                </InputGroups>

                {/* ── Description Section ── */}
                <InputGroups icon="/icons/inputs-group-icon.svg" title={t.tierForm.descriptionSection}>
                    <div className="tf-row tf-row--2">
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label={`${t.tierForm.description} (${t.tierForm.arabic})`}
                            value={form.descriptionAr}
                            onChange={(e) => setField("descriptionAr", e.target.value)}
                            sx={textFieldSxRtl}
                        />
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label={`${t.tierForm.description} (${t.tierForm.english})`}
                            value={form.descriptionEn}
                            onChange={(e) => setField("descriptionEn", e.target.value)}
                            sx={textFieldSxLtr}
                        />
                    </div>
                </InputGroups>

                {/* ── Points Configuration Section ── */}
                <InputGroups icon="/icons/inputs-group-icon.svg" title={t.tierForm.coffeeBeansSection}>
                    <div className="tf-row tf-row--2">
                        <TextField
                            fullWidth
                            type="number"
                            label={t.tierForm.start}
                            value={form.start}
                            onChange={(e) => setField("start", e.target.value)}
                            slotProps={{ htmlInput: { min: 0, step: 1 } }}
                            sx={getTextFieldSx(dir as "rtl" | "ltr")}
                        />
                        <TextField
                            fullWidth
                            type="number"
                            label={t.tierForm.redeemRate}
                            value={form.redeemRate}
                            onChange={(e) => setField("redeemRate", e.target.value)}
                            slotProps={{ htmlInput: { min: 1, step: 1 } }}
                            sx={getTextFieldSx(dir as "rtl" | "ltr")}
                        />
                    </div>
                    <p className="tf-hint">{t.tierForm.ratesHint}</p>
                </InputGroups>
            </div>

            {/* ── Footer ── */}
            <div className="tf-footer">
                <button type="button" className="tf-btn tf-btn--cancel" onClick={onClose} disabled={submitting}>
                    {t.tierForm.cancel}
                </button>
                <button type="submit" className="tf-btn tf-btn--submit" disabled={submitting}>
                    {submitting ? <span className="tf-spinner" /> : isEdit ? t.tierForm.update : t.tierForm.create}
                </button>
            </div>
        </form>
    );
}
